import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { X, Mail } from "lucide-react";
import { format } from 'date-fns';

interface FeedbackDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  feedback: {
    id: string;
    feedback: string;
    created_at: string;
    can_contact: boolean;
    status: string;
    email: string | null;
    rating: string;
  } | null;
}

export const FeedbackDetailsSheet = ({ isOpen, onClose, feedback }: FeedbackDetailsProps) => {
  if (!feedback) return null;

  const getEmojiForRating = (rating: string) => {
    switch (rating) {
      case 'very_negative': return '😖';
      case 'negative': return '😡';
      case 'neutral': return '😐';
      case 'positive': return '🙂';
      case 'very_positive': return '😍';
      default: return '❓';
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
        <SheetHeader className="mb-6">
          <div className="flex justify-between items-center">
            <SheetTitle className="text-xl font-semibold text-[#333B44]">
              Feedback Details
            </SheetTitle>
            <SheetClose asChild>
              <Button variant="ghost" size="icon">
                <X className="h-4 w-4" />
              </Button>
            </SheetClose>
          </div>
        </SheetHeader>

        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-medium text-[#5A636E] mb-1">Submitted On</h3>
            <p className="text-[#333B44]">{format(new Date(feedback.created_at), 'PPpp')}</p>
          </div>

          <div>
            <h3 className="text-sm font-medium text-[#5A636E] mb-1">Rating</h3>
            <p className="text-2xl">{getEmojiForRating(feedback.rating)}</p>
          </div>

          <div>
            <h3 className="text-sm font-medium text-[#5A636E] mb-1">Feedback</h3>
            <p className="text-[#333B44] whitespace-pre-wrap">{feedback.feedback}</p>
          </div>

          <div>
            <h3 className="text-sm font-medium text-[#5A636E] mb-1">Contact Information</h3>
            {feedback.email ? (
              <a
                href={`mailto:${feedback.email}`}
                className="inline-flex items-center text-[#3BAFDA] hover:text-[#333B44]"
              >
                <Mail className="h-4 w-4 mr-2" />
                {feedback.email}
              </a>
            ) : (
              <p className="text-[#5A636E]">No email provided</p>
            )}
            <p className="text-[#5A636E] mt-1">
              Can be contacted: {feedback.can_contact ? 'Yes' : 'No'}
            </p>
          </div>

          <div>
            <h3 className="text-sm font-medium text-[#5A636E] mb-1">Status</h3>
            <p className="capitalize text-[#333B44]">{feedback.status}</p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
